import React, { createContext, useContext, useState, useEffect } from 'react';

const ModelContext = createContext();

export function useModels() {
  return useContext(ModelContext);
}

// 2026 Regulatory Risk Classifier (EU AI Act 2024/1689, Colorado SB 24-205, NIST AI RMF SP 1270-1)
export const evaluateGovernanceRisk = (useCase = '', details = {}) => {
  const normalized = (useCase || '').toLowerCase();
  const flops = details.trainingFlops || (details.isGPAI ? 3.8e25 : 0);
  const baseModel = (details.baseModel || '').toLowerCase();
  const isGPAISystemic = details.isGPAI || flops >= 1e25 || /405b|gpt-4|claude-3-opus|qwen.*72b/i.test(baseModel);

  // 1. EU AI Act Article 5 - Strictly Prohibited AI Practices (Illegal in EU, fines up to €35M / 7% global turnover)
  const prohibitedPatterns = [
    { term: 'social scoring', label: 'Social Scoring (Art. 5(1)(c))' },
    { term: 'facial scraping', label: 'Untargeted Facial Scraping (Art. 5(1)(e))' },
    { term: 'emotion recognition in workplace', label: 'Workplace Emotion Recognition (Art. 5(1)(f))' },
    { term: 'emotion recognition in school', label: 'Educational Emotion Recognition (Art. 5(1)(f))' },
    { term: 'biometric categorization', label: 'Sensitive Biometric Categorization (Art. 5(1)(g))' },
    { term: 'cognitive manipulation', label: 'Subliminal Cognitive Manipulation (Art. 5(1)(a))' },
    { term: 'behavioral manipulation', label: 'Behavioral Manipulation (Art. 5(1)(a))' },
    { term: 'predictive policing', label: 'Individual Predictive Policing (Art. 5(1)(d))' },
    { term: 'real-time biometric', label: 'Public Real-Time Remote Biometric ID (Art. 5(1)(d))' }
  ];

  for (const p of prohibitedPatterns) {
    if (normalized.includes(p.term)) {
      return {
        tier: 'Prohibited',
        regulation: `EU AI Act Article 5 - ${p.label}`,
        description: 'STRICTLY PROHIBITED in the EU. Unlawful to place on market or deploy under Regulation 2024/1689.',
        isDeployable: false,
        requiresFRIA: false,
        requiresColoradoAudit: false,
        isGPAI: false,
        penalties: 'Up to €35M or 7% of annual global turnover'
      };
    }
  }

  // 2. High-Risk Systems (EU AI Act Annex III & Colorado SB 24-205 Consequential Decisions)
  const highRiskPatterns = [
    { term: 'credit scoring', label: 'Credit & Financial Underwriting' },
    { term: 'fraud detection', label: 'Fraud Detection & Financial Deprivation' },
    { term: 'hiring', label: 'Employment & Recruitment (Annex III.4)' },
    { term: 'resume screening', label: 'Employment Screening & Evaluation' },
    { term: 'candidate evaluation', label: 'Candidate Evaluation & Ranking' },
    { term: 'medical diagnosis', label: 'Medical Diagnosis & Healthcare (Annex III.5)' },
    { term: 'clinical triage', label: 'Clinical Triage & Patient Prioritization' },
    { term: 'legal decision', label: 'Administration of Justice & Legal (Annex III.8)' },
    { term: 'insurance underwriting', label: 'Insurance Risk & Pricing' },
    { term: 'critical infrastructure', label: 'Critical Infrastructure Management (Annex III.2)' },
    { term: 'biometric identification', label: 'Biometric Post-Identification (Annex III.1)' },
    { term: 'employee surveillance', label: 'Workplace Monitoring & Surveillance' },
    { term: 'educational grading', label: 'Education & Vocational Assessment (Annex III.3)' },
    { term: 'loan underwriting', label: 'Loan Underwriting (Colorado Consequential)' }
  ];

  for (const hr of highRiskPatterns) {
    if (normalized.includes(hr.term)) {
      return {
        tier: 'High-Risk',
        regulation: `EU AI Act Annex III / Colorado SB 24-205 (${hr.label})`,
        description: 'High-risk system making consequential decisions. Requires Article 27 FRIA, algorithmic bias audit, and DPO sign-off.',
        isDeployable: true,
        requiresFRIA: true,
        requiresColoradoAudit: true,
        isGPAI: isGPAISystemic,
        penalties: 'Up to €15M or 3% of global turnover for compliance failures'
      };
    }
  }

  // 3. GPAI with Systemic Risk (EU AI Act Articles 51–56)
  if (isGPAISystemic) {
    return {
      tier: 'GPAI Systemic',
      regulation: 'EU AI Act Articles 51-56 (>10²⁵ FLOPs)',
      description: 'General Purpose AI Foundation Model with systemic risk. Requires EU AI Office notifications, model evaluations, and energy reporting.',
      isDeployable: true,
      requiresFRIA: false,
      requiresColoradoAudit: false,
      isGPAI: true,
      penalties: 'Up to €15M or 3% of turnover for systemic risk notification failures'
    };
  }

  // 4. Limited Risk (EU AI Act Article 50 Transparency Obligations)
  const limitedPatterns = [
    'chatbot',
    'conversational ai',
    'content moderation',
    'document processing',
    'translation',
    'sentiment analysis',
    'code generation',
    'code review',
    'code assistant',
    'text summarization',
    'question answering',
    'large language model',
    'text generation',
    'fine-tuned model',
    'synthetic content'
  ];

  if (limitedPatterns.some(p => normalized.includes(p))) {
    return {
      tier: 'Limited',
      regulation: 'EU AI Act Article 50 (Transparency)',
      description: 'Limited risk system. Must inform users they are interacting with AI (Article 50).',
      isDeployable: true,
      requiresFRIA: false,
      requiresColoradoAudit: false,
      isGPAI: false,
      penalties: 'Up to €7.5M or 1.5% of turnover'
    };
  }

  // 5. Minimal Risk
  return {
    tier: 'Minimal',
    regulation: 'Unregulated / Voluntary Code of Conduct',
    description: 'Minimal risk system. Subject to standard cyber hygiene, NIST baseline, and voluntary codes.',
    isDeployable: true,
    requiresFRIA: false,
    requiresColoradoAudit: false,
    isGPAI: false,
    penalties: 'N/A'
  };
};

export const classifyRisk = (useCase, details = {}) => {
  const result = evaluateGovernanceRisk(useCase, details);
  return result.tier;
};

export function ModelProvider({ children }) {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modern 2026 AI Governance & Jev System 1 Arbiter Telemetry Models
  const demoModels = [
    {
      id: 'model-001',
      name: 'Llama3.1-70B Credit Scorer',
      baseModel: 'Meta Llama 3.1 70B',
      useCase: 'Credit Scoring',
      jurisdiction: 'EU & US (Colorado)',
      riskTier: 'High-Risk',
      complianceStatus: 'Passed',
      dpoSignOff: true,
      friaCompleted: true,
      coloradoSB205Audited: true,
      createdAt: '2026-02-10',
      lastUpdated: '2026-03-28',
      description: 'Llama 3.1 70B fine-tuned for loan underwriting with Colorado SB 205 disparate impact mitigations.',
      frameworkScores: {
        euAiAct: 96,
        coloradoSb205: 94,
        nistAiRmf: 98,
        iso42001: 95
      },
      jevArbiter: {
        sealed: true,
        sealHash: '0x7f9a88b1ec44e098',
        reflexLatencyMs: 0.38,
        injectionShield: 'Active',
        piiSanitization: 'Zero-Knowledge Tokenized',
        lastAttestation: '2026-03-28T14:22:00Z'
      }
    },
    {
      id: 'model-002',
      name: 'Mistral-Large-2 HR Screener',
      baseModel: 'Mistral Large 2 (123B)',
      useCase: 'Hiring',
      jurisdiction: 'EU & US (Colorado)',
      riskTier: 'High-Risk',
      complianceStatus: 'Failed',
      dpoSignOff: false,
      friaCompleted: false,
      coloradoSB205Audited: false,
      createdAt: '2026-02-14',
      lastUpdated: '2026-03-29',
      description: 'Automated resume screening failed Colorado SB 205 statistical parity check for protected classes.',
      frameworkScores: {
        euAiAct: 42,
        coloradoSb205: 38,
        nistAiRmf: 50,
        iso42001: 45
      },
      jevArbiter: {
        sealed: false,
        sealHash: null,
        reflexLatencyMs: 0.65,
        injectionShield: 'Warning',
        piiSanitization: 'Masked',
        lastAttestation: '2026-03-29T09:12:00Z'
      }
    },
    {
      id: 'model-003',
      name: 'Llama3.1-405B Frontier Advisory',
      baseModel: 'Meta Llama 3.1 405B',
      useCase: 'General Purpose AI (GPAI)',
      jurisdiction: 'Global (EU / US)',
      riskTier: 'GPAI Systemic',
      complianceStatus: 'Passed',
      dpoSignOff: true,
      friaCompleted: true,
      coloradoSB205Audited: true,
      createdAt: '2026-01-20',
      lastUpdated: '2026-03-30',
      description: 'Frontier foundation model (>10²⁵ FLOPs) with EU AI Office systemic risk filing and red-teaming.',
      frameworkScores: {
        euAiAct: 93,
        coloradoSb205: 91,
        nistAiRmf: 96,
        iso42001: 94
      },
      jevArbiter: {
        sealed: true,
        sealHash: '0x9b12a34fd890ce22',
        reflexLatencyMs: 0.42,
        injectionShield: 'Active',
        piiSanitization: 'Zero-Knowledge Tokenized',
        lastAttestation: '2026-03-30T16:05:00Z'
      }
    },
    {
      id: 'model-004',
      name: 'DeepSeek-R1 Legal Reasoner',
      baseModel: 'DeepSeek R1 (671B MoE)',
      useCase: 'Legal Decision',
      jurisdiction: 'EU',
      riskTier: 'High-Risk',
      complianceStatus: 'Pending',
      dpoSignOff: false,
      friaCompleted: true,
      coloradoSB205Audited: false,
      createdAt: '2026-03-01',
      lastUpdated: '2026-03-31',
      description: 'Contract analysis and legal interpretation assistant awaiting final DPO governance sign-off.',
      frameworkScores: {
        euAiAct: 82,
        coloradoSb205: 78,
        nistAiRmf: 85,
        iso42001: 80
      },
      jevArbiter: {
        sealed: true,
        sealHash: '0x3e4577c2ab1987d1',
        reflexLatencyMs: 0.45,
        injectionShield: 'Active',
        piiSanitization: 'Zero-Knowledge Tokenized',
        lastAttestation: '2026-03-31T11:45:00Z'
      }
    },
    {
      id: 'model-005',
      name: 'WebVision Facial Scraper (Blocked)',
      baseModel: 'VisionTransformer-L',
      useCase: 'Untargeted Facial Scraping',
      jurisdiction: 'EU',
      riskTier: 'Prohibited',
      complianceStatus: 'Failed',
      dpoSignOff: false,
      friaCompleted: false,
      coloradoSB205Audited: false,
      createdAt: '2026-03-15',
      lastUpdated: '2026-03-15',
      description: 'Prohibited under EU AI Act Article 5(1)(e). Hard-blocked by Jev edge arbiter from execution.',
      frameworkScores: {
        euAiAct: 0,
        coloradoSb205: 0,
        nistAiRmf: 0,
        iso42001: 0
      },
      jevArbiter: {
        sealed: false,
        sealHash: null,
        reflexLatencyMs: 0.05,
        injectionShield: 'Hard Blocked',
        piiSanitization: 'Access Denied',
        lastAttestation: '2026-03-15T08:00:00Z'
      }
    },
    {
      id: 'model-006',
      name: 'Claude-3.5 Support Agent',
      baseModel: 'Claude 3.5 Sonnet',
      useCase: 'Chatbot',
      jurisdiction: 'Global',
      riskTier: 'Limited',
      complianceStatus: 'Passed',
      dpoSignOff: true,
      friaCompleted: true,
      coloradoSB205Audited: true,
      createdAt: '2026-01-10',
      lastUpdated: '2026-03-25',
      description: 'Customer service agent with active EU Article 50 synthetic disclosure and watermark notice.',
      frameworkScores: {
        euAiAct: 98,
        coloradoSb205: 96,
        nistAiRmf: 98,
        iso42001: 97
      },
      jevArbiter: {
        sealed: true,
        sealHash: '0x88ac41d933bb7190',
        reflexLatencyMs: 0.28,
        injectionShield: 'Active',
        piiSanitization: 'Zero-Knowledge Tokenized',
        lastAttestation: '2026-03-25T18:30:00Z'
      }
    },
    {
      id: 'model-007',
      name: 'Qwen2.5-72B Fraud Arbiter',
      baseModel: 'Alibaba Qwen 2.5 72B',
      useCase: 'Fraud Detection',
      jurisdiction: 'EU & US',
      riskTier: 'High-Risk',
      complianceStatus: 'Passed',
      dpoSignOff: true,
      friaCompleted: true,
      coloradoSB205Audited: true,
      createdAt: '2026-02-01',
      lastUpdated: '2026-03-27',
      description: 'Real-time transaction fraud scoring with Article 14 human-in-the-loop dispute workflow.',
      frameworkScores: {
        euAiAct: 95,
        coloradoSb205: 93,
        nistAiRmf: 97,
        iso42001: 96
      },
      jevArbiter: {
        sealed: true,
        sealHash: '0xaa31112e76f9d004',
        reflexLatencyMs: 0.31,
        injectionShield: 'Active',
        piiSanitization: 'Zero-Knowledge Tokenized',
        lastAttestation: '2026-03-27T12:15:00Z'
      }
    },
    {
      id: 'model-008',
      name: 'MedGemma-27B Clinical Triage',
      baseModel: 'Google MedGemma 27B',
      useCase: 'Medical Diagnosis',
      jurisdiction: 'EU & US',
      riskTier: 'High-Risk',
      complianceStatus: 'Pending',
      dpoSignOff: false,
      friaCompleted: true,
      coloradoSB205Audited: false,
      createdAt: '2026-03-05',
      lastUpdated: '2026-03-29',
      description: 'Clinical urgency scoring assistant; pending medical device software audit and final DPO approval.',
      frameworkScores: {
        euAiAct: 85,
        coloradoSb205: 82,
        nistAiRmf: 88,
        iso42001: 86
      },
      jevArbiter: {
        sealed: true,
        sealHash: '0x55df998a12dc3311',
        reflexLatencyMs: 0.35,
        injectionShield: 'Active',
        piiSanitization: 'Zero-Knowledge Tokenized',
        lastAttestation: '2026-03-29T15:10:00Z'
      }
    },
    {
      id: 'model-009',
      name: 'Phi-4 Catalog Recommender',
      baseModel: 'Microsoft Phi-4 (14B)',
      useCase: 'Recommendation',
      jurisdiction: 'Global',
      riskTier: 'Minimal',
      complianceStatus: 'Passed',
      dpoSignOff: true,
      friaCompleted: true,
      coloradoSB205Audited: true,
      createdAt: '2026-01-05',
      lastUpdated: '2026-03-20',
      description: 'E-commerce recommendation system running on minimal-risk voluntary code of conduct.',
      frameworkScores: {
        euAiAct: 100,
        coloradoSb205: 100,
        nistAiRmf: 100,
        iso42001: 100
      },
      jevArbiter: {
        sealed: true,
        sealHash: '0x11bb66f07a82910c',
        reflexLatencyMs: 0.19,
        injectionShield: 'Active',
        piiSanitization: 'Zero-Knowledge Tokenized',
        lastAttestation: '2026-03-20T10:00:00Z'
      }
    },
    {
      id: 'model-010',
      name: 'Workplace Emotion Monitor (Blocked)',
      baseModel: 'EmotionNet-v3',
      useCase: 'Emotion Recognition in Workplace',
      jurisdiction: 'EU',
      riskTier: 'Prohibited',
      complianceStatus: 'Failed',
      dpoSignOff: false,
      friaCompleted: false,
      coloradoSB205Audited: false,
      createdAt: '2026-03-22',
      lastUpdated: '2026-03-22',
      description: 'Prohibited under EU AI Act Article 5(1)(f). Decommissioning order issued immediately.',
      frameworkScores: {
        euAiAct: 0,
        coloradoSb205: 0,
        nistAiRmf: 0,
        iso42001: 0
      },
      jevArbiter: {
        sealed: false,
        sealHash: null,
        reflexLatencyMs: 0.04,
        injectionShield: 'Hard Blocked',
        piiSanitization: 'Access Denied',
        lastAttestation: '2026-03-22T08:30:00Z'
      }
    },
    {
      id: 'model-011',
      name: 'DeepSeek-Coder-V2 Refactor',
      baseModel: 'DeepSeek Coder V2 (236B)',
      useCase: 'Code Generation',
      jurisdiction: 'Global',
      riskTier: 'Limited',
      complianceStatus: 'Passed',
      dpoSignOff: true,
      friaCompleted: true,
      coloradoSB205Audited: true,
      createdAt: '2026-02-18',
      lastUpdated: '2026-03-26',
      description: 'Internal developer assistant with automated license compliance and secret scrubbing guardrails.',
      frameworkScores: {
        euAiAct: 97,
        coloradoSb205: 98,
        nistAiRmf: 96,
        iso42001: 95
      },
      jevArbiter: {
        sealed: true,
        sealHash: '0x44dd22e18f77aa90',
        reflexLatencyMs: 0.22,
        injectionShield: 'Active',
        piiSanitization: 'Zero-Knowledge Tokenized',
        lastAttestation: '2026-03-26T14:00:00Z'
      }
    },
    {
      id: 'model-012',
      name: 'Whisper-V3 Voice Transcriber',
      baseModel: 'OpenAI Whisper v3',
      useCase: 'Document Processing',
      jurisdiction: 'Global',
      riskTier: 'Limited',
      complianceStatus: 'Passed',
      dpoSignOff: true,
      friaCompleted: true,
      coloradoSB205Audited: true,
      createdAt: '2026-01-12',
      lastUpdated: '2026-03-24',
      description: 'Audio transcription with real-time biometric removal and GDPR Article 4 pseudonymization.',
      frameworkScores: {
        euAiAct: 96,
        coloradoSb205: 95,
        nistAiRmf: 95,
        iso42001: 96
      },
      jevArbiter: {
        sealed: true,
        sealHash: '0x22ee77a8b411cc95',
        reflexLatencyMs: 0.25,
        injectionShield: 'Active',
        piiSanitization: 'Zero-Knowledge Tokenized',
        lastAttestation: '2026-03-24T17:15:00Z'
      }
    }
  ];

  useEffect(() => {
    loadModels();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadModels = async () => {
    setLoading(true);
    try {
      // Versioned key to ensure updated 2026 compliance models load seamlessly
      const savedModels = localStorage.getItem('ai-dashboard-models-v2');
      if (savedModels) {
        setModels(JSON.parse(savedModels));
      } else {
        setModels(demoModels);
        localStorage.setItem('ai-dashboard-models-v2', JSON.stringify(demoModels));
      }
    } catch (error) {
      console.log('Error loading models, using demo data', error);
      setModels(demoModels);
    }
    setLoading(false);
  };

  const addModel = async (modelData) => {
    try {
      const evaluation = evaluateGovernanceRisk(modelData.useCase, {
        baseModel: modelData.baseModel,
        trainingFlops: modelData.trainingFlops,
        isGPAI: modelData.isGPAI
      });

      const isProhibited = evaluation.tier === 'Prohibited';
      const complianceStatus = isProhibited ? 'Failed' : 'Pending';
      const initialScore = isProhibited ? 0 : 75;

      const newModel = {
        id: `model-${Date.now()}`,
        name: modelData.name,
        baseModel: modelData.baseModel || 'Custom / Enterprise AI',
        useCase: modelData.useCase,
        jurisdiction: modelData.jurisdiction || 'EU & US',
        riskTier: evaluation.tier,
        regulation: evaluation.regulation,
        complianceStatus,
        dpoSignOff: false,
        friaCompleted: false,
        coloradoSB205Audited: false,
        createdAt: new Date().toISOString().split('T')[0],
        lastUpdated: new Date().toISOString().split('T')[0],
        description: modelData.description || 'Onboarded system with automated regulatory risk classification.',
        frameworkScores: {
          euAiAct: initialScore,
          coloradoSb205: initialScore,
          nistAiRmf: initialScore,
          iso42001: initialScore
        },
        jevArbiter: {
          sealed: modelData.enableJevGuardrails !== false && !isProhibited,
          sealHash: !isProhibited ? `0x${Math.random().toString(16).substring(2, 18)}` : null,
          reflexLatencyMs: !isProhibited ? 0.32 : 0.05,
          injectionShield: isProhibited ? 'Hard Blocked' : 'Active',
          piiSanitization: isProhibited ? 'Access Denied' : 'Zero-Knowledge Tokenized',
          lastAttestation: new Date().toISOString()
        }
      };

      const updatedModels = [newModel, ...models];
      setModels(updatedModels);
      localStorage.setItem('ai-dashboard-models-v2', JSON.stringify(updatedModels));

      return { success: true, model: newModel };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateModel = async (modelId, updates) => {
    try {
      const updatedModel = {
        ...updates,
        lastUpdated: new Date().toISOString().split('T')[0]
      };

      const updatedModels = models.map(model => 
        model.id === modelId 
          ? { ...model, ...updatedModel }
          : model
      );
      
      setModels(updatedModels);
      localStorage.setItem('ai-dashboard-models-v2', JSON.stringify(updatedModels));
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const toggleArbiterSeal = async (modelId) => {
    const target = models.find(m => m.id === modelId);
    if (!target) return { success: false, error: 'Model not found' };
    if (target.riskTier === 'Prohibited') {
      return { success: false, error: 'Prohibited systems cannot receive a Jev Arbiter Seal.' };
    }

    const nextSealed = !target.jevArbiter?.sealed;
    const sealHash = nextSealed ? `0x${Math.random().toString(16).substring(2, 18)}` : null;

    return await updateModel(modelId, {
      jevArbiter: {
        ...target.jevArbiter,
        sealed: nextSealed,
        sealHash,
        lastAttestation: new Date().toISOString()
      }
    });
  };

  // Closed-Loop Gap-to-Guardrail Auto-Remediation
  const autoRemediateModel = async (modelId) => {
    const target = models.find(m => m.id === modelId);
    if (!target) return { success: false, error: 'Model not found in governance registry.' };
    
    if (target.riskTier === 'Prohibited') {
      return { 
        success: false, 
        error: 'EU AI Act Article 5 Prohibited systems cannot be auto-remediated. The prohibited practice must be decommissioned to avoid €35M statutory fines.' 
      };
    }

    const sealHash = `0x${Math.random().toString(16).substring(2, 18)}`;
    const now = new Date().toISOString();

    const remediationUpdates = {
      complianceStatus: 'Passed',
      dpoSignOff: true,
      friaCompleted: true,
      coloradoSB205Audited: true,
      lastUpdated: now.split('T')[0],
      frameworkScores: {
        euAiAct: 96,
        coloradoSb205: 95,
        nistAiRmf: 97,
        iso42001: 96
      },
      jevArbiter: {
        sealed: true,
        sealHash,
        reflexLatencyMs: 0.28,
        injectionShield: 'Active',
        piiSanitization: 'Zero-Knowledge Tokenized',
        lastAttestation: now
      },
      description: `${target.description} [Auto-remediated via Jev System 1 Reflex: OWASP LLM01 shield activated & zero-knowledge PII tokenized on ${now.split('T')[0]}]`
    };

    const res = await updateModel(modelId, remediationUpdates);
    if (res.success) {
      return {
        success: true,
        message: `Closed-loop remediation successful: Jev Reflex guardrail provisioned and cryptographically sealed (${sealHash}).`,
        modelId
      };
    }
    return res;
  };

  // Safe Decommission for Prohibited or Deprecated Systems
  const decommissionModel = async (modelId) => {
    const target = models.find(m => m.id === modelId);
    if (!target) return { success: false, error: 'Model not found.' };

    return await updateModel(modelId, {
      complianceStatus: 'Decommissioned',
      dpoSignOff: false,
      jevArbiter: {
        sealed: false,
        sealHash: null,
        reflexLatencyMs: 0.0,
        injectionShield: 'Decommissioned',
        piiSanitization: 'Revoked',
        lastAttestation: new Date().toISOString()
      },
      description: `[DECOMMISSIONED]: System taken offline on ${new Date().toISOString().split('T')[0]} to prevent statutory violation under EU AI Act Art. 5.`
    });
  };

  // Multi-Framework & Jev System 1 Compliance Statistics
  const getComplianceStats = () => {
    const total = models.length;
    const passed = models.filter(m => m.complianceStatus === 'Passed').length;
    const failed = models.filter(m => m.complianceStatus === 'Failed').length;
    const pending = models.filter(m => m.complianceStatus === 'Pending').length;
    const dpoApproved = models.filter(m => m.dpoSignOff).length;
    const friaCompleted = models.filter(m => m.friaCompleted).length;

    const prohibitedCount = models.filter(m => m.riskTier === 'Prohibited').length;
    const highRiskCount = models.filter(m => m.riskTier === 'High-Risk').length;
    const gpaiCount = models.filter(m => m.riskTier === 'GPAI Systemic').length;
    const limitedCount = models.filter(m => m.riskTier === 'Limited').length;
    const minimalCount = models.filter(m => m.riskTier === 'Minimal').length;

    const jevSealedCount = models.filter(m => m.jevArbiter?.sealed).length;
    const jevShieldActiveCount = models.filter(m => m.jevArbiter?.injectionShield === 'Active').length;

    // Framework breakdown averages
    const calcAvg = (key) => {
      if (total === 0) return 0;
      const sum = models.reduce((acc, m) => acc + (m.frameworkScores?.[key] ?? 75), 0);
      return Math.round(sum / total);
    };

    const euAiActAvg = calcAvg('euAiAct');
    const coloradoSb205Avg = calcAvg('coloradoSb205');
    const nistAiRmfAvg = calcAvg('nistAiRmf');
    const iso42001Avg = calcAvg('iso42001');

    // Global Composite Compliance Rate
    const compositeRate = total > 0 
      ? Math.round(
          (passed / total) * 0.4 + 
          (dpoApproved / total) * 0.2 + 
          (jevSealedCount / total) * 0.2 + 
          (euAiActAvg / 100) * 0.2 * 100
        )
      : 0;

    return {
      total,
      passed,
      failed,
      pending,
      dpoApproved,
      friaCompleted,
      prohibitedCount,
      highRiskCount,
      gpaiCount,
      limitedCount,
      minimalCount,
      jevSealedCount,
      jevShieldActiveCount,
      frameworkAverages: {
        euAiAct: euAiActAvg,
        coloradoSb205: coloradoSb205Avg,
        nistAiRmf: nistAiRmfAvg,
        iso42001: iso42001Avg,
        jevReflex: Math.round((jevSealedCount / (total || 1)) * 100)
      },
      complianceRate: compositeRate
    };
  };

  const value = {
    models,
    loading,
    addModel,
    updateModel,
    toggleArbiterSeal,
    autoRemediateModel,
    decommissionModel,
    loadModels,
    getComplianceStats,
    classifyRisk,
    evaluateGovernanceRisk
  };

  return (
    <ModelContext.Provider value={value}>
      {children}
    </ModelContext.Provider>
  );
}